/**
 * DesignSpec — the structured JSON source of truth for a Komai design.
 *
 * OpenSCAD code is a DERIVED artifact of this spec, produced by renderSpec().
 * All tool calls in the agent loop mutate a DesignSpec; the renderer is a pure
 * function with no I/O.
 *
 * Schemas are duplicated with functions/src/shared/spec.ts and should stay in
 * sync with that file. (Same convention as shared/types.ts ↔
 * functions/src/shared/types.ts.)
 */

import { z } from 'zod';

// ─── Geometry primitives ───────────────────────────────────────────────────

export const Vec3Schema = z.tuple([z.number(), z.number(), z.number()]);
export const EulerSchema = z.tuple([z.number(), z.number(), z.number()]);

// ─── Materials ─────────────────────────────────────────────────────────────

export const MaterialKindSchema = z.enum([
  'plywood',
  'hardwood',
  'softwood',
  'mdf',
  'sheet_metal',
  'printed_pla',
  'printed_abs',
  'printed_petg',
  'custom',
]);

export const MaterialSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  kind: MaterialKindSchema,
  thickness: z.number().positive().optional(),
  density: z.number().positive().optional(),
  color: z.string().optional(),
});

// ─── Image assets / visual surface applications ───────────────────────────

export const ImageAssetSchema = z.object({
  id: z.string().min(1),
  storagePath: z.string().min(1),
  contentType: z.string().regex(/^image\//),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  fileName: z.string().optional(),
  kind: z.enum(['reference', 'decal', 'texture', 'label']).default('decal'),
  createdAt: z.string().optional(),
});

const SurfaceImageFaceSchema = z.enum([
  'top', 'bottom', 'front', 'back', 'left', 'right',
]);

export const SurfaceImageTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('object_top') }),
  z.object({
    kind: z.literal('part_face'),
    partId: z.string().min(1),
    face: SurfaceImageFaceSchema.default('top'),
  }),
  z.object({ kind: z.literal('part'), partId: z.string().min(1) }),
]);

export const SurfaceImageSchema = z.object({
  id: z.string().min(1),
  assetId: z.string().min(1),
  target: SurfaceImageTargetSchema.default({ kind: 'object_top' }),
  mode: z.enum(['decal', 'repeat_texture', 'label']).default('decal'),
  center: z.tuple([z.number(), z.number()]).default([0, 0]),
  size: z.tuple([z.number().positive(), z.number().positive()]),
  rotationDeg: z.number().default(0),
  opacity: z.number().min(0).max(1).default(1),
  renderOrder: z.number().int().default(0),
  locked: z.boolean().default(false),
});

// ─── Parameters (with optional safe expression) ────────────────────────────

export const SpecParameterSchema = z.object({
  name: z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, 'must be a valid identifier'),
  displayName: z.string(),
  value: z.union([z.number(), z.string(), z.boolean()]),
  defaultValue: z.union([z.number(), z.string(), z.boolean()]),
  type: z.enum(['string', 'number', 'boolean']).optional(),
  description: z.string().optional(),
  group: z.string().optional(),
  /** Optional expression like "total_width * 0.5". Evaluated by a safe parser,
   *  never via eval(). Leave undefined for plain literal parameters. */
  expression: z.string().optional(),
  range: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      step: z.number().optional(),
    })
    .optional(),
});

// ─── Parts (tagged union) ──────────────────────────────────────────────────

/** Maps a numeric field on a part (or joint) to a named parameter. When a
 *  ref is present, renderSpec emits the parameter name in the generated
 *  OpenSCAD instead of the literal value — so the Parameters tab can tweak
 *  the dimension without re-prompting the AI. Field names are dot-paths
 *  ("width", "depth", "crossSection.side", etc.). */
export const ParamRefSchema = z.object({
  field: z.string().min(1),
  param: z.string().min(1),
});

// ─── Detail Features ───────────────────────────────────────────────────────

export const FeatureAxisSchema = z.enum(['x', 'y', 'z']);
export const FeatureFaceSchema = z.enum([
  'top',
  'bottom',
  'left',
  'right',
  'front',
  'back',
]);

/** Drawing-engine callout fields. Populated by builders and renderers when
 *  deterministic. Consumed by drawing-metadata derivation (hole tables, leader
 *  notes, section/detail hints) and by B6's downstream drawing service. */
export const FeatureCalloutSchema = z
  .object({
    /** Label rendered in hole table or leader note, e.g. "M3 H7", "⌀5 +0.05/-0". */
    label: z.string().optional(),
    /** Nominal size string for hole-table rows, e.g. "M3", "⌀5". */
    nominalSize: z.string().optional(),
    /** True when the hole/pocket goes all the way through the part. */
    through: z.boolean().optional(),
    /** Thread note for tapped holes, e.g. "M3×0.5 ▽". */
    threadNote: z.string().optional(),
    /** Fit class, e.g. "H7", "h6". */
    fitClass: z.string().optional(),
    /** Tolerance class, e.g. "fine", "standard". */
    toleranceClass: z.string().optional(),
    /** Head style for screw-related holes, e.g. "socket", "flat", "pan". */
    headStyle: z.string().optional(),
    /** Hint to drawing engines, e.g. "detail-view-needed", "section-needed". */
    sectionDetailHint: z.string().optional(),
  })
  .strict();

export const FeaturePatternSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('linear'),
    count: z.number().int().positive(),
    spacing: z.number().positive(),
    axis: FeatureAxisSchema,
  }),
  z.object({
    type: z.literal('grid'),
    rows: z.number().int().positive(),
    columns: z.number().int().positive(),
    spacingX: z.number().positive(),
    spacingY: z.number().positive(),
  }),
]);

const featureBaseFields = {
  id: z.string().min(1),
  partId: z.string().min(1),
  name: z.string().optional(),
  /** Local position relative to the target part's anchor/origin, in mm. */
  position: Vec3Schema.default([0, 0, 0]),
  /** Local rotation applied after position, in degrees. */
  rotation: EulerSchema.default([0, 0, 0]),
  /** Optional semantic face hint used by prompts, validation, and drawings. */
  face: FeatureFaceSchema.optional(),
  /** Repeats this feature from its local position. */
  pattern: FeaturePatternSchema.optional(),
  /** Explicit additional local positions. If set, these override pattern. */
  positions: z.array(Vec3Schema).optional(),
  notes: z.string().optional(),
  /** Optional field->parameter references for retunable feature dimensions. */
  paramRefs: z.array(ParamRefSchema).default([]),
};

export const ScrewHoleFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('screw_hole'),
  size: z.string().min(1),
  length: z.number().positive(),
  head: z.enum(['none', 'flat', 'socket', 'hex', 'button', 'pan', 'cheese']).default('flat'),
  axis: FeatureAxisSchema.default('z'),
  thread: z.union([z.boolean(), z.number()]).default(false),
  tolerance: z.string().optional(),
  oversize: z.number().optional(),
  holeOversize: z.number().optional(),
  headOversize: z.number().optional(),
  slop: z.number().nonnegative().optional(),
  counterbore: z.union([z.boolean(), z.number()]).optional(),
  teardrop: z.boolean().default(false),
});

export const InsertHoleFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('insert_hole'),
  insertType: z.enum(['heat_set', 'threaded', 'press_fit']).default('heat_set'),
  size: z.string().min(1).optional(),
  outerDiameter: z.number().positive(),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  flangeDiameter: z.number().positive().optional(),
});

export const DowelHoleFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('dowel_hole'),
  diameter: z.number().positive(),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  through: z.boolean().default(false),
});

export const SlotFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('slot'),
  length: z.number().positive(),
  width: z.number().positive(),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  through: z.boolean().default(true),
  roundedEnds: z.boolean().default(true),
});

export const BossFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('boss'),
  diameter: z.number().positive(),
  height: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  holeDiameter: z.number().positive().optional(),
  ribCount: z.number().int().nonnegative().default(0),
});

export const RibFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('rib'),
  length: z.number().positive(),
  height: z.number().positive(),
  thickness: z.number().positive(),
  axis: z.enum(['x', 'y']).default('x'),
  taper: z.number().min(0).max(1).optional(),
});

export const VentGridFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('vent_grid'),
  rows: z.number().int().positive(),
  columns: z.number().int().positive(),
  pitchX: z.number().positive(),
  pitchY: z.number().positive(),
  holeDiameter: z.number().positive().optional(),
  slotWidth: z.number().positive().optional(),
  slotLength: z.number().positive().optional(),
  depth: z.number().positive().optional(),
  axis: FeatureAxisSchema.default('z'),
});

export const EngravingFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('engraving'),
  text: z.string().min(1),
  size: z.number().positive(),
  depth: z.number().positive().default(0.4),
  font: z.string().optional(),
  halign: z.enum(['left', 'center', 'right']).default('center'),
  valign: z.enum(['top', 'center', 'bottom', 'baseline']).default('center'),
});

export const EdgeTreatmentFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('edge_treatment'),
  treatment: z.enum(['roundover', 'chamfer']),
  amount: z.number().positive(),
  scope: z.enum(['all', 'top', 'bottom', 'vertical']).default('all'),
});

export const PocketFeatureSchema = z
  .object({
    ...featureBaseFields,
    kind: z.literal('pocket'),
    shape: z.enum(['cylindrical', 'rectangular', 'conical']).default('cylindrical'),
    diameter: z.number().positive().optional(),
    width: z.number().positive().optional(),
    length: z.number().positive().optional(),
    topDiameter: z.number().positive().optional(),
    bottomDiameter: z.number().positive().optional(),
    depth: z.number().positive(),
    axis: FeatureAxisSchema.default('z'),
    bottom: z.enum(['flat', 'drill_point']).default('flat'),
    purpose: z
      .enum(['generic', 'hinge_cup', 'bearing_seat', 'recess', 'button'])
      .default('generic'),
    /** Provenance of dimensions when sourced from hardwareCatalog (e.g. "HINGE_CUP.cupDiameter"). */
    catalogSource: z.string().optional(),
    /** Drawing-engine callout fields; populated when deterministic. */
    callout: FeatureCalloutSchema.optional(),
  })
  .superRefine((p, ctx) => {
    if (p.shape === 'cylindrical' && p.diameter == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'cylindrical pocket requires `diameter`' });
    }
    if (p.shape === 'rectangular' && (p.width == null || p.length == null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'rectangular pocket requires both `width` and `length`' });
    }
    if (p.shape === 'conical' && (p.topDiameter == null || p.bottomDiameter == null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'conical pocket requires both `topDiameter` and `bottomDiameter`' });
    }
  });

export const CounterboreFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('counterbore'),
  diameter: z.number().positive(),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  /** Optional bind to a screw_hole feature so renderer/validation can verify
   *  the counterbore is concentric with and larger than the through-hole. */
  targetFeatureId: z.string().optional(),
  /** Provenance of dimensions when sourced from hardwareCatalog (e.g. "METRIC_SCREWS.M3.recommendedCounterboreDepth"). */
  catalogSource: z.string().optional(),
  /** Drawing-engine callout fields; populated when deterministic. */
  callout: FeatureCalloutSchema.optional(),
});

export const CountersinkFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('countersink'),
  diameter: z.number().positive(),
  /** Included angle in degrees (90 = ISO standard for flat-head metric screws). */
  angle: z.number().positive().default(90),
  /** Optional explicit depth. If omitted, renderer derives from diameter and angle. */
  depth: z.number().positive().optional(),
  axis: FeatureAxisSchema.default('z'),
  targetFeatureId: z.string().optional(),
  /** Provenance of dimensions when sourced from hardwareCatalog (e.g. "METRIC_SCREWS.M3.countersunkHeadDiameter"). */
  catalogSource: z.string().optional(),
  /** Drawing-engine callout fields; populated when deterministic. */
  callout: FeatureCalloutSchema.optional(),
});

export const ThreadedHoleFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('threaded_hole'),
  /** Thread designation, e.g. "M3", "M4". */
  size: z.string().min(1),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  /** Optional explicit thread pitch in mm; if omitted the renderer/agent picks the standard. */
  pitch: z.number().positive().optional(),
  /** Optional thread fit class, e.g. "6H". */
  threadClass: z.string().optional(),
  /** True for blind tap, false for through-tap. */
  blind: z.boolean().default(true),
  /** Lead-in chamfer depth at the hole entry, mm. */
  leadInChamfer: z.number().nonnegative().default(0.5),
  /** Provenance of dimensions when sourced from hardwareCatalog. */
  catalogSource: z.string().optional(),
  /** Drawing-engine callout fields. */
  callout: FeatureCalloutSchema.optional(),
});

export const NutTrapFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('nut_trap'),
  size: z.string().min(1),
  nutType: z.enum(['hex', 'square']).default('hex'),
  /** Optional explicit across-flats. If omitted, renderer reads `HEX_NUTS[size].acrossFlats`. */
  acrossFlats: z.number().positive().optional(),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  /** "top" = nut inserted from outside before assembly; "side" = nut slides in from a side slot. */
  access: z.enum(['top', 'side']).default('top'),
  clearance: z.number().nonnegative().default(0.2),
  catalogSource: z.string().optional(),
  callout: FeatureCalloutSchema.optional(),
});

export const DovetailFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('dovetail'),
  /** "tail" = additive trapezoid on the bearing part; "socket" = subtractive on the receiving part. */
  role: z.enum(['tail', 'socket']),
  length: z.number().positive(),
  width: z.number().positive(),
  depth: z.number().positive(),
  /** Half-angle in degrees from the axial direction (8° = standard hardwood dovetail). */
  angle: z.number().positive().default(8),
  axis: FeatureAxisSchema.default('x'),
  clearance: z.number().nonnegative().default(0.15),
  catalogSource: z.string().optional(),
  callout: FeatureCalloutSchema.optional(),
});

export const CableGlandFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('cable_gland'),
  /** Thread designation, e.g. "M12", "M16", "PG7". */
  threadSize: z.string().min(1),
  /** Through-hole diameter (clearance for the gland body). */
  holeDiameter: z.number().positive(),
  /** Optional flange diameter; renderer reserves space if present. */
  flangeDiameter: z.number().positive().optional(),
  depth: z.number().positive(),
  axis: FeatureAxisSchema.default('z'),
  sealGroove: z.boolean().default(false),
  catalogSource: z.string().optional(),
  callout: FeatureCalloutSchema.optional(),
});

export const BearingSeatFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('bearing_seat'),
  /** Bearing designation, e.g. "608", "625", "6001". */
  bearingType: z.string().min(1),
  /** Outer diameter of the seat. Should match the bearing OD ± fit allowance. */
  diameter: z.number().positive(),
  depth: z.number().positive(),
  /** Mechanical fit. "press" = interference, "snug" = transition, "slip" = clearance. */
  fit: z.enum(['slip', 'snug', 'press']).default('snug'),
  axis: FeatureAxisSchema.default('z'),
  /** True for an inner shoulder (e.g. for axial bearing retention). */
  retainingLip: z.boolean().default(false),
  catalogSource: z.string().optional(),
  callout: FeatureCalloutSchema.optional(),
});

export const KeywayFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('keyway'),
  width: z.number().positive(),
  depth: z.number().positive(),
  length: z.number().positive(),
  axis: FeatureAxisSchema.default('x'),
  /** "rounded" = end-milled (typical), "square" = ends squared off. */
  endStyle: z.enum(['square', 'rounded']).default('rounded'),
  catalogSource: z.string().optional(),
  callout: FeatureCalloutSchema.optional(),
});

export const SnapFitFeatureSchema = z.object({
  ...featureBaseFields,
  kind: z.literal('snap_fit'),
  /** "tab" = additive cantilever beam with a hook; "receiver" = subtractive pocket that catches the tab. */
  role: z.enum(['tab', 'receiver']),
  length: z.number().positive(),
  width: z.number().positive(),
  thickness: z.number().positive(),
  /** Hook bump height (only meaningful for "tab" role). */
  hookHeight: z.number().positive().optional(),
  clearance: z.number().nonnegative().default(0.25),
  axis: FeatureAxisSchema.default('x'),
  catalogSource: z.string().optional(),
  callout: FeatureCalloutSchema.optional(),
});

export const FeatureSchema = z.discriminatedUnion('kind', [
  ScrewHoleFeatureSchema,
  InsertHoleFeatureSchema,
  DowelHoleFeatureSchema,
  SlotFeatureSchema,
  BossFeatureSchema,
  RibFeatureSchema,
  VentGridFeatureSchema,
  EngravingFeatureSchema,
  EdgeTreatmentFeatureSchema,
  PocketFeatureSchema,
  CounterboreFeatureSchema,
  CountersinkFeatureSchema,
  ThreadedHoleFeatureSchema,
  NutTrapFeatureSchema,
  DovetailFeatureSchema,
  CableGlandFeatureSchema,
  BearingSeatFeatureSchema,
  KeywayFeatureSchema,
  SnapFitFeatureSchema,
]);

const partBaseFields = {
  id: z.string().min(1),
  name: z.string(),
  materialId: z.string().min(1),
  position: Vec3Schema.default([0, 0, 0]),
  rotation: EulerSchema.default([0, 0, 0]),
  tags: z.array(z.string()).default([]),
  notes: z.string().optional(),
  /** Optional field→parameter references for re-tunable dimensions. */
  paramRefs: z.array(ParamRefSchema).default([]),
};

export const PanelCutoutSchema = z.object({
  shape: z.enum(['rect', 'circle']),
  x: z.number(),
  y: z.number(),
  w: z.number().positive().optional(),
  h: z.number().positive().optional(),
  d: z.number().positive().optional(),
  through: z.boolean().default(true),
});

export const PanelPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('panel'),
  width: z.number().positive(),
  depth: z.number().positive(),
  thickness: z.number().positive(),
  rounding: z.number().nonnegative().optional(),
  cutouts: z.array(PanelCutoutSchema).default([]),
});

export const CrossSectionSchema = z.discriminatedUnion('shape', [
  z.object({ shape: z.literal('square'), side: z.number().positive() }),
  z.object({ shape: z.literal('circle'), diameter: z.number().positive() }),
  z.object({
    shape: z.literal('rect'),
    width: z.number().positive(),
    depth: z.number().positive(),
  }),
]);

export const LegPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('leg'),
  length: z.number().positive(),
  crossSection: CrossSectionSchema,
  taper: z
    .object({
      topScale: z.number().positive().max(1).default(1),
      startFraction: z.number().min(0).max(1).default(0),
    })
    .optional(),
});

export const RailPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('rail'),
  length: z.number().positive(),
  width: z.number().positive(),
  thickness: z.number().positive(),
});

export const ShelfPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('shelf'),
  width: z.number().positive(),
  depth: z.number().positive(),
  thickness: z.number().positive(),
});

export const FastenerPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('fastener'),
  fastenerType: z.enum(['screw', 'bolt', 'dowel', 'nail']),
  /** e.g. "M3", "M4", "M5" */
  size: z.string().min(1),
  length: z.number().positive(),
  head: z.enum(['none', 'flat', 'socket', 'hex', 'button', 'pan', 'cheese']).default('flat'),
  quantity: z.number().int().positive().default(1),
});

export const CustomPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('custom'),
  /** Raw OpenSCAD emitted inside the module body. Bypasses structural hooks. */
  openscadSnippet: z.string(),
  boundingBox: z
    .object({
      w: z.number().positive(),
      d: z.number().positive(),
      h: z.number().positive(),
    })
    .optional(),
});

/** Window/door cutout in a wall. x/z are offsets from the wall's bottom-center. */
export const WallOpeningSchema = z.object({
  kind: z.enum(['window', 'door']),
  /** Horizontal offset from wall center, in mm. */
  x: z.number(),
  /** Vertical offset from wall bottom, in mm (ground-level for doors = 0). */
  z: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});

/** A vertical wall panel — for architectural / building models. Semantics:
 *  width = horizontal extent along the wall's length
 *  height = vertical extent
 *  thickness = wall thickness (depth, usually 100-300mm at real scale, or
 *              2-5mm at 1:100 scale).
 *  position is the wall's base-center. rotation[2] (z-axis yaw) rotates it. */
export const WallPartSchema = z.object({
  ...partBaseFields,
  kind: z.literal('wall'),
  width: z.number().positive(),
  height: z.number().positive(),
  thickness: z.number().positive(),
  openings: z.array(WallOpeningSchema).default([]),
});

export const PartSchema = z.discriminatedUnion('kind', [
  PanelPartSchema,
  LegPartSchema,
  RailPartSchema,
  ShelfPartSchema,
  FastenerPartSchema,
  CustomPartSchema,
  WallPartSchema,
]);

// ─── Joints (tagged union) ─────────────────────────────────────────────────

const jointBaseFields = {
  id: z.string().min(1),
  fromPartId: z.string().min(1),
  toPartId: z.string().min(1),
  notes: z.string().optional(),
};

/** Placement information needed to render a joint into actual geometry.
 *  fromPosition / toPosition are local to each part's anchor/origin. */
const JointPlacementSchema = z.object({
  fromFace: FeatureFaceSchema.optional(),
  toFace: FeatureFaceSchema.optional(),
  fromPosition: Vec3Schema,
  toPosition: Vec3Schema,
  axis: FeatureAxisSchema.default('x'),
});

export const MortiseTenonJointSchema = z.object({
  ...jointBaseFields,
  ...JointPlacementSchema.shape,
  type: z.literal('mortise_tenon'),
  tenonWidth: z.number().positive(),
  tenonHeight: z.number().positive(),
  tenonLength: z.number().positive(),
  /** Distance from the part edge to the tenon shoulder (mm). */
  shoulder: z.number().nonnegative().default(0),
  /** Whether the tenon includes a haunch (typical for top rail joints). */
  haunched: z.boolean().default(false),
  /** Clearance between mortise pocket and tenon, mm. Default 0.2 (typical for hardwood). */
  clearance: z.number().nonnegative().default(0.2),
});

export const DadoJointSchema = z.object({
  ...jointBaseFields,
  type: z.literal('dado'),
  width: z.number().positive(),
  depth: z.number().positive(),
  length: z.number().positive(),
});

export const DowelJointSchema = z.object({
  ...jointBaseFields,
  type: z.literal('dowel'),
  diameter: z.number().positive(),
  length: z.number().positive(),
  count: z.number().int().positive().default(2),
});

export const ScrewJointSchema = z.object({
  ...jointBaseFields,
  type: z.literal('screw'),
  size: z.string().min(1),
  length: z.number().positive(),
  count: z.number().int().positive().default(1),
});

export const BiscuitJointSchema = z.object({
  ...jointBaseFields,
  type: z.literal('biscuit'),
  size: z.enum(['0', '10', '20']),
  count: z.number().int().positive().default(2),
});

export const GlueJointSchema = z.object({
  ...jointBaseFields,
  type: z.literal('glue'),
});

export const RabbetJointSchema = z.object({
  ...jointBaseFields,
  ...JointPlacementSchema.shape,
  type: z.literal('rabbet'),
  /** Rabbet width — the depth of cut into the receiving panel's face. */
  width: z.number().positive(),
  /** Rabbet depth — the height of the L-step (typically the thickness of the inserted panel). */
  depth: z.number().positive(),
  /** Run length along the panel edge. */
  length: z.number().positive(),
  clearance: z.number().nonnegative().default(0.1),
});

export const TongueGrooveJointSchema = z.object({
  ...jointBaseFields,
  ...JointPlacementSchema.shape,
  type: z.literal('tongue_groove'),
  /** Tongue width — narrow dimension of the protrusion. */
  tongueWidth: z.number().positive(),
  /** Tongue depth — how far the tongue extends out of the bearing part. */
  tongueDepth: z.number().positive(),
  /** Run length along the joint axis. */
  length: z.number().positive(),
  clearance: z.number().nonnegative().default(0.15),
});

export const JointSchema = z.discriminatedUnion('type', [
  MortiseTenonJointSchema,
  DadoJointSchema,
  DowelJointSchema,
  ScrewJointSchema,
  BiscuitJointSchema,
  GlueJointSchema,
  RabbetJointSchema,
  TongueGrooveJointSchema,
]);

// ─── View hints ────────────────────────────────────────────────────────────

export const ViewHintSchema = z.object({
  label: z.string(),
  camera: z.enum([
    'front',
    'top',
    'right',
    'left',
    'rear',
    'bottom',
    'isometric',
    'custom',
  ]),
  customAngles: Vec3Schema.optional(),
});

// ─── Drawing-grade callouts (GD&T, surface finish, fit/tolerance) ─────────
// Mirror of functions/src/shared/spec.ts. Per
// docs/superpowers/specs/2026-04-26-gdt-surface-finish-tolerance-symbols-design.md
// (rev 3.2). See that doc for the payload×target validity matrix and GD&T
// datum-count semantics.

export const CadCategorySchema = z.enum([
  'furniture',
  'mechanical',
  'architecture',
  'printing',
]);

export const DimensionToleranceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('symmetric'),
    plusMinus: z.number().nonnegative(),
  }),
  z.object({
    kind: z.literal('asymmetric'),
    plus: z.number().nonnegative(),
    minus: z.number().nonpositive(),
  }),
  z.object({
    kind: z.literal('limits'),
    low: z.number(),
    high: z.number(),
  }).refine((t) => t.low <= t.high, {
    message: 'limits.low must be <= limits.high',
  }),
  z.object({
    kind: z.literal('fit_class'),
    designation: z.string().regex(/^[A-Za-z]+\d+(\/[A-Za-z]+\d+)?$/),
  }),
  z.object({ kind: z.literal('reference') }),
  z.object({ kind: z.literal('basic') }),
]);

export const SurfaceFinishSchema = z.object({
  ra: z.number().positive().optional(),
  rz: z.number().positive().optional(),
  symbol: z.enum(['basic', 'machining_required', 'machining_prohibited']).default('basic'),
  direction: z
    .enum([
      'parallel', 'perpendicular', 'crossed', 'multi',
      'circular', 'radial', 'particulate',
    ])
    .optional(),
  method: z.string().max(40).optional(),
});

const GDT_FORM = ['flatness', 'straightness', 'circularity', 'cylindricity'] as const;
const GDT_PROFILE = ['profile_line', 'profile_surface'] as const;
const GDT_ORIENTATION = ['perpendicularity', 'parallelism', 'angularity'] as const;
const GDT_LOCATION = ['position', 'concentricity', 'symmetry'] as const;
const GDT_RUNOUT = ['runout', 'total_runout'] as const;

export const GdtCalloutSchema = z
  .object({
    type: z.enum([
      ...GDT_FORM, ...GDT_PROFILE, ...GDT_ORIENTATION, ...GDT_LOCATION, ...GDT_RUNOUT,
    ]),
    tolerance: z.number().positive(),
    modifier: z.enum(['M', 'L', 'P', 'F']).optional(),
    datums: z.array(z.string().regex(/^[A-Z]$/)).max(3).optional(),
  })
  .superRefine((g, ctx) => {
    const n = g.datums?.length ?? 0;
    if ((GDT_FORM as readonly string[]).includes(g.type) && n > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `gdt_datum_semantics:${g.type}_with_datums — form tolerance must not reference datums`,
      });
    }
    if ((GDT_ORIENTATION as readonly string[]).includes(g.type) && n < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `gdt_datum_semantics:${g.type}_no_datum — orientation tolerance requires at least one datum`,
      });
    }
    if ((GDT_LOCATION as readonly string[]).includes(g.type) && n < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `gdt_datum_semantics:${g.type}_no_datum — location tolerance requires at least one datum`,
      });
    }
    if ((GDT_RUNOUT as readonly string[]).includes(g.type) && n !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `gdt_datum_semantics:${g.type}_datum_count — runout requires exactly one datum`,
      });
    }
  });

const DrawingFaceLabelSchema = z.enum([
  'top', 'bottom', 'front', 'back', 'left', 'right',
  'inner', 'outer', 'centerline',
]);

export const DrawingCalloutTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('feature'),   featureId: z.string() }),
  z.object({ kind: z.literal('part_face'), partId: z.string(), face: DrawingFaceLabelSchema }),
  z.object({ kind: z.literal('part'),      partId: z.string() }),
]);

export const DrawingCalloutSchema = z
  .object({
    id: z.string(),
    target: DrawingCalloutTargetSchema,
    surfaceFinish: SurfaceFinishSchema.optional(),
    gdt: GdtCalloutSchema.optional(),
    datumLabel: z.string().regex(/^[A-Z]$/).optional(),
    tolerance: DimensionToleranceSchema.optional(),
    note: z.string().max(200).optional(),
    preferredView: z
      .enum(['top', 'front', 'right', 'left', 'rear', 'bottom', 'isometric'])
      .optional(),
    preferredSide: z.enum(['top', 'bottom', 'left', 'right']).optional(),
  })
  .superRefine((c, ctx) => {
    const hasPayload = !!(c.surfaceFinish || c.gdt || c.datumLabel || c.tolerance || c.note);
    if (!hasPayload) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'callout_target_payload:empty_callout — must carry at least one payload',
      });
      return;
    }
    if (c.target.kind === 'part') {
      if (c.gdt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'callout_target_payload:gdt_on_part — gdt must target a feature or part_face',
        });
      }
      if (c.datumLabel) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'callout_target_payload:datumLabel_on_part — a whole part is not a datum reference',
        });
      }
      if (c.tolerance) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'callout_target_payload:tolerance_on_part — tolerance must target a feature',
        });
      }
    }
    if (c.target.kind === 'part_face' && c.tolerance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'callout_target_payload:tolerance_on_part_face — tolerance must target a feature',
      });
    }
  });

export const DefaultToleranceSchema = z.discriminatedUnion('standard', [
  z.object({
    standard: z.literal('iso2768'),
    linearClass: z.enum(['f', 'm', 'c', 'v']),
    geometricalClass: z.enum(['H', 'K', 'L']).optional(),
  }),
  z.object({
    standard: z.literal('asme_y14_5'),
    blockTolerance: z.string().min(1).max(80),
  }),
  z.object({
    standard: z.literal('jis_b_0405'),
    linearClass: z.enum(['f', 'm', 'c', 'v']),
  }),
]);

// ─── Top-level DesignSpec ──────────────────────────────────────────────────

export const DesignSpecSchema = z.object({
  specVersion: z.literal('1'),
  title: z.string(),
  units: z.enum(['mm', 'inch']).default('mm'),
  qualityLevel: z.enum(['draft', 'standard', 'manufacturing', 'extreme']).default('standard'),
  category: CadCategorySchema.default('furniture'),
  parameters: z.array(SpecParameterSchema).default([]),
  materials: z.array(MaterialSchema).default([]),
  imageAssets: z.array(ImageAssetSchema).default([]),
  surfaceImages: z.array(SurfaceImageSchema).default([]),
  parts: z.array(PartSchema).default([]),
  features: z.array(FeatureSchema).default([]),
  joints: z.array(JointSchema).default([]),
  viewHints: z.array(ViewHintSchema).default([]),
  drawingCallouts: z.array(DrawingCalloutSchema).default([]),
  defaultTolerance: DefaultToleranceSchema.optional(),
  tolerancingStandard: z.enum(['asme', 'iso', 'jis']).default('iso'),
  derived: z
    .object({
      openscadCode: z.string().optional(),
      codeHash: z.string().optional(),
      generatedAt: z.string().optional(),
    })
    .optional(),
});

// ─── Inferred types ────────────────────────────────────────────────────────

export type ParamRef = z.infer<typeof ParamRefSchema>;
export type Vec3 = z.infer<typeof Vec3Schema>;
export type Euler = z.infer<typeof EulerSchema>;
export type FeatureAxis = z.infer<typeof FeatureAxisSchema>;
export type FeatureFace = z.infer<typeof FeatureFaceSchema>;
export type FeaturePattern = z.infer<typeof FeaturePatternSchema>;
export type ImageAsset = z.infer<typeof ImageAssetSchema>;
export type SurfaceImageTarget = z.infer<typeof SurfaceImageTargetSchema>;
export type SurfaceImage = z.infer<typeof SurfaceImageSchema>;
export type ScrewHoleFeature = z.infer<typeof ScrewHoleFeatureSchema>;
export type InsertHoleFeature = z.infer<typeof InsertHoleFeatureSchema>;
export type DowelHoleFeature = z.infer<typeof DowelHoleFeatureSchema>;
export type SlotFeature = z.infer<typeof SlotFeatureSchema>;
export type BossFeature = z.infer<typeof BossFeatureSchema>;
export type RibFeature = z.infer<typeof RibFeatureSchema>;
export type VentGridFeature = z.infer<typeof VentGridFeatureSchema>;
export type EngravingFeature = z.infer<typeof EngravingFeatureSchema>;
export type EdgeTreatmentFeature = z.infer<typeof EdgeTreatmentFeatureSchema>;
export type FeatureCallout = z.infer<typeof FeatureCalloutSchema>;
export type PocketFeature = z.infer<typeof PocketFeatureSchema>;
export type CounterboreFeature = z.infer<typeof CounterboreFeatureSchema>;
export type CountersinkFeature = z.infer<typeof CountersinkFeatureSchema>;
export type Feature = z.infer<typeof FeatureSchema>;
export type MaterialKind = z.infer<typeof MaterialKindSchema>;
export type Material = z.infer<typeof MaterialSchema>;
export type SpecParameter = z.infer<typeof SpecParameterSchema>;
export type PanelCutout = z.infer<typeof PanelCutoutSchema>;
export type PanelPart = z.infer<typeof PanelPartSchema>;
export type LegPart = z.infer<typeof LegPartSchema>;
export type RailPart = z.infer<typeof RailPartSchema>;
export type ShelfPart = z.infer<typeof ShelfPartSchema>;
export type FastenerPart = z.infer<typeof FastenerPartSchema>;
export type CustomPart = z.infer<typeof CustomPartSchema>;
export type WallPart = z.infer<typeof WallPartSchema>;
export type WallOpening = z.infer<typeof WallOpeningSchema>;
export type Part = z.infer<typeof PartSchema>;
export type MortiseTenonJoint = z.infer<typeof MortiseTenonJointSchema>;
export type DadoJoint = z.infer<typeof DadoJointSchema>;
export type DowelJoint = z.infer<typeof DowelJointSchema>;
export type ScrewJoint = z.infer<typeof ScrewJointSchema>;
export type BiscuitJoint = z.infer<typeof BiscuitJointSchema>;
export type GlueJoint = z.infer<typeof GlueJointSchema>;
export type ThreadedHoleFeature = z.infer<typeof ThreadedHoleFeatureSchema>;
export type NutTrapFeature = z.infer<typeof NutTrapFeatureSchema>;
export type DovetailFeature = z.infer<typeof DovetailFeatureSchema>;
export type CableGlandFeature = z.infer<typeof CableGlandFeatureSchema>;
export type BearingSeatFeature = z.infer<typeof BearingSeatFeatureSchema>;
export type KeywayFeature = z.infer<typeof KeywayFeatureSchema>;
export type SnapFitFeature = z.infer<typeof SnapFitFeatureSchema>;
export type RabbetJoint = z.infer<typeof RabbetJointSchema>;
export type TongueGrooveJoint = z.infer<typeof TongueGrooveJointSchema>;
export type Joint = z.infer<typeof JointSchema>;
export type ViewHint = z.infer<typeof ViewHintSchema>;
export type CadCategory = z.infer<typeof CadCategorySchema>;
export type DimensionTolerance = z.infer<typeof DimensionToleranceSchema>;
export type SurfaceFinish = z.infer<typeof SurfaceFinishSchema>;
export type GdtCallout = z.infer<typeof GdtCalloutSchema>;
export type DrawingCalloutTarget = z.infer<typeof DrawingCalloutTargetSchema>;
export type DrawingCallout = z.infer<typeof DrawingCalloutSchema>;
export type DefaultTolerance = z.infer<typeof DefaultToleranceSchema>;
export type TolerancingStandard = 'asme' | 'iso' | 'jis';
export type DesignSpec = z.infer<typeof DesignSpecSchema>;
